import useTableRequest from "../../hooks/useTableRequest";
import TableState from "../../components/common/table/TableState";
import { useCallback, useEffect, useRef, useState } from "react";
import moment from "jalali-moment";
import Title from "../../components/common/title/Title";
import ExportExcel from "../../components/common/export-exel/ExportExel";

import { scheduleContentScroll } from "../../utils/scroll/pageScroll";

const monthNames = [
  "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];
const currentYear = Number(moment().format("jYYYY"));
const currentMonth = Number(moment().format("jMM"));

export default function MonthlyOrderCohort() {
  const resultsStart = useRef(null);
  const [year, setYear] = useState(currentYear);
  const [monthNumber, setMonthNumber] = useState(currentMonth);
  const [maxBefore, setMaxBefore] = useState("1");
  const [maxAfter, setMaxAfter] = useState("2");
  const [page, setPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({ year: currentYear, monthNumber: currentMonth, maxBefore: "1", maxAfter: "2" });
  const [result, setResult] = useState(null);
  const { load: request, loading, error, retry } = useTableRequest();

  const load = useCallback(async (nextPage, filters) => {
    return request({
        path: "admin/reports/monthlyOrderCohort",
        params: {
          month: `${filters.year}-${String(filters.monthNumber).padStart(2, "0")}`,
          maxBefore: filters.maxBefore,
          maxAfter: filters.maxAfter,
          page: nextPage,
          limit: 25,
        },
      }, (response) => {
      setResult(response.data.result);
      setPage(nextPage);
      setAppliedFilters(filters);
    });
  }, [request]);

  useEffect(() => {
    load(1, { year: currentYear, monthNumber: currentMonth, maxBefore: "1", maxAfter: "2" });
  }, [load]);

  const changePage = (nextPage) => {
    load(nextPage, appliedFilters);
    scheduleContentScroll(resultsStart.current);
  };

  const filters = { year, monthNumber, maxBefore, maxAfter };
  const totalPages = result ? Math.ceil(result.total / result.limit) : 0;
  const exportRows = (result?.items || []).map((item) => ({
    "شناسه کاربر": item.userId,
    "نام کاربر": item.name,
    "موبایل": item.mobile,
    "نقش": item.role,
    "سفارش قبل از ماه": item.beforeCount,
    "سفارش ماه انتخابی": item.monthOrderCount,
    "سفارش بعد از ماه": item.afterCount,
  }));

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Title title="رفتار سفارش کاربران در یک ماه" />
        {result && <ExportExcel jsonData={exportRows} disabled={loading || error} />}
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        کاربران دارای سفارش پرداخت‌شده در ماه شمسی انتخابی را نشان می‌دهد. دو عدد، حداکثر تعداد سفارش پرداخت‌شدهٔ همان کاربر را به‌ترتیب پیش از شروع ماه و پس از پایان ماه تعیین می‌کنند. سفارش‌های خود ماه جداگانه شمرده می‌شوند. خروجی اکسل مربوط به صفحهٔ نمایش‌داده‌شده است.
      </p>
      <form onSubmit={(event) => { event.preventDefault(); load(1, filters); }} className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm dark:text-white">سال شمسی
          <input type="number" min="1300" max="1499" step="1" required className="admin-field" value={year} onChange={(event) => setYear(Number(event.target.value))} />
        </label>
        <label className="flex flex-col gap-1 text-sm dark:text-white">ماه شمسی
          <select className="admin-field" value={monthNumber} onChange={(event) => setMonthNumber(Number(event.target.value))}>
            {monthNames.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm dark:text-white">حداکثر سفارش قبل از ماه
          <input type="number" min="0" max="1000" step="1" required value={maxBefore} onChange={(event) => setMaxBefore(event.target.value)} className="admin-field" />
        </label>
        <label className="flex flex-col gap-1 text-sm dark:text-white">حداکثر سفارش بعد از ماه
          <input type="number" min="0" max="1000" step="1" required value={maxAfter} onChange={(event) => setMaxAfter(event.target.value)} className="admin-field" />
        </label>
        <button type="submit" disabled={loading} className="rounded-lg bg-blue-700 px-4 py-2 text-white disabled:opacity-50">نمایش کاربران</button>
      </form>
      <div ref={resultsStart} data-list-start />
      {(loading || error) && <TableState loading={loading} error={error} onRetry={retry} />}
      {!loading && !error && result && <p className="text-sm dark:text-white">تعداد کاربران مطابق شرایط: {result.total}</p>}
      <div className="overflow-x-auto" hidden={loading || error}>
        <table className="w-full text-sm text-right dark:text-white">
          <thead><tr className="bg-gray-100 dark:bg-gray-800">
            <th className="p-2">نام</th><th className="p-2">موبایل</th><th className="p-2">شناسه کاربر</th><th className="p-2">قبل از ماه</th><th className="p-2">ماه انتخابی</th><th className="p-2">بعد از ماه</th>
          </tr></thead>
          <tbody>{result?.items?.map((item) => <tr key={item.userId} className="border-b">
            <td className="p-2">{item.name || "—"}</td><td className="p-2" dir="ltr">{item.mobile || "—"}</td><td className="p-2" dir="ltr">{item.userId}</td>
            <td className="p-2">{item.beforeCount}</td><td className="p-2">{item.monthOrderCount}</td><td className="p-2">{item.afterCount}</td>
          </tr>)}</tbody>
        </table>
        {!loading && result && !result.items.length && <p className="p-4 text-sm text-gray-500">کاربری با این شرایط پیدا نشد.</p>}
      </div>
      {!error && totalPages > 1 && <div className="flex items-center gap-3 dark:text-white">
        <button type="button" disabled={loading || page <= 1} onClick={() => changePage(page - 1)} className="rounded border px-3 py-1 disabled:opacity-50">صفحه قبل</button>
        <span>صفحه {page} از {totalPages}</span>
        <button type="button" disabled={loading || page >= totalPages} onClick={() => changePage(page + 1)} className="rounded border px-3 py-1 disabled:opacity-50">صفحه بعد</button>
      </div>}
    </div>
  );
}
